import { type MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { formatDate } from "@/shared/utils";
import {
  ESTADO_CONTACTO_LABEL,
  estadoColor,
} from "./useFidelizacionCampana";
import type { FidelizacionContacto } from "../fidelizacion-campana.interface";

const Vacio = ({ children = "—" }: { children?: string | number }) => (
  <Typography variant="caption" color="text.secondary">
    {children}
  </Typography>
);

///* Columnas del preview del lote. Por defecto solo se ven 4 (contacto, teléfono, estado
///* y envío); las de detalle se activan desde el menu de columnas de la tabla.
export const useColumnsFidelizacion = () => {
  const columns = useMemo<MRT_ColumnDef<FidelizacionContacto>[]>(
    () => [
      {
        accessorKey: "fila",
        header: "FILA",
        size: 70,
        enableHiding: true,
        Cell: ({ row }) => <Vacio>{row.original.fila}</Vacio>,
      },
      {
        id: "contacto",
        header: "CONTACTO",
        size: 250,
        accessorFn: (row) =>
          [row.nombre, row.apellido].filter(Boolean).join(" ") || row.cedula,
        Cell: ({ row }) => {
          const original = row.original;
          const nombre = [original.nombre, original.apellido]
            .filter(Boolean)
            .join(" ");
          const secondaire = [original.cedula, original.id_contrato]
            .filter(Boolean)
            .join(" · ");
          return (
            <Box sx={{ lineHeight: 1.35 }}>
              <Typography variant="body2" noWrap>
                {nombre || <Vacio>sin nombre</Vacio>}
              </Typography>
              {secondaire && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {secondaire}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "telefono_1",
        header: "TELÉFONO",
        size: 150,
        Cell: ({ row }) => {
          const original = row.original;
          if (!original.telefono_1) return <Vacio>sin teléfono</Vacio>;
          return (
            <Tooltip
              title={
                original.telefono_enviado
                  ? `Enviado como ${original.telefono_enviado}`
                  : "Se normaliza a formato internacional al enviar"
              }
            >
              <Typography variant="body2" noWrap>
                {original.telefono_1}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "estado",
        header: "ESTADO",
        size: 130,
        Cell: ({ row }) => {
          const estado = row.original.estado;
          const detalle = row.original.detalle_error;
          const chip = (
            <Chip
              size="small"
              label={ESTADO_CONTACTO_LABEL[estado] ?? estado}
              color={estadoColor(estado)}
              variant="outlined"
            />
          );
          if (!detalle) return chip;
          return (
            <Tooltip title={detalle}>
              <Box sx={{ lineHeight: 1.3 }}>
                {chip}
                <Typography
                  variant="caption"
                  color="error"
                  noWrap
                  sx={{ display: "block", maxWidth: 200 }}
                >
                  {detalle}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        id: "envio",
        header: "ENVÍO",
        size: 150,
        accessorFn: (row) => row.fecha_envio ?? "",
        Cell: ({ row }) => {
          const original = row.original;
          if (!original.enviado) return <Vacio>pendiente</Vacio>;
          return (
            <Stack spacing={0.25} sx={{ lineHeight: 1.3 }}>
              <Chip
                size="small"
                label="Enviado"
                color="info"
                variant="outlined"
                sx={{ width: "fit-content" }}
              />
              {original.fecha_envio && (
                <Typography variant="caption" color="text.secondary">
                  {formatDate(original.fecha_envio)}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        accessorKey: "parametros",
        header: "PARÁMETROS HSM",
        size: 200,
        enableHiding: true,
        Cell: ({ row }) =>
          row.original.parametros ? (
            <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
              {row.original.parametros}
            </Typography>
          ) : (
            <Vacio />
          ),
      },
      {
        accessorKey: "respuesta_hsm",
        header: "RESPUESTA HSM",
        size: 240,
        enableHiding: true,
        Cell: ({ row }) => {
          const respuesta = row.original.respuesta_hsm;
          if (!respuesta) return <Vacio />;
          return (
            <Tooltip title={respuesta}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {respuesta}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "detalle_error",
        header: "DETALLE",
        size: 220,
        enableHiding: true,
        Cell: ({ row }) => {
          const detalle = row.original.detalle_error;
          if (!detalle) return <Vacio />;
          return (
            <Tooltip title={detalle}>
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {detalle}
              </Typography>
            </Tooltip>
          );
        },
      },
    ],
    [],
  );

  return { columns };
};
